import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { HOME } from '@/constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8;

const formatGBP = (amount) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--brand-parchment)] flex flex-col">
      <Navigation />
      <main className="flex-1 flex items-center justify-center px-6 py-32 lg:py-40">
        <div className="max-w-2xl w-full">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

// UK postcode (loose validation — Stripe already verified payment, we just want
// to avoid obvious junk before posting). Allow standard outward+inward formats.
const UK_POSTCODE_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

function PaidView({ data, sessionId, onBack }) {
  const [submitted, setSubmitted] = useState(
    Boolean(data?.address_captured),
  );
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    postal_code: '',
  });

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Inline validation
    const trimmed = {
      ...form,
      full_name: form.full_name.trim(),
      line1: form.line1.trim(),
      line2: form.line2.trim() || undefined,
      city: form.city.trim(),
      postal_code: form.postal_code.trim().toUpperCase(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
    };
    if (!trimmed.full_name || !trimmed.line1 || !trimmed.city || !trimmed.postal_code) {
      setFormError('Please fill in name, address line 1, town/city and postcode.');
      return;
    }
    if (!UK_POSTCODE_RE.test(trimmed.postal_code)) {
      setFormError('Hmm, that doesn\u2019t look like a valid UK postcode. Try again?');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/checkout/address/${sessionId}`, trimmed);
      setSubmitted(true);
    } catch (err) {
      setFormError(
        err?.response?.data?.detail ||
          'Could not save your address. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation screen, after address has been captured
  if (submitted) {
    return (
      <div
        data-testid="checkout-address-confirmed"
        className="bg-white border border-[var(--brand-stone)] p-10 lg:p-14 text-center"
      >
        <CheckCircle2
          size={64}
          className="mx-auto text-[var(--brand-olive)]"
          strokeWidth={1.5}
        />
        <span
          data-testid={HOME.checkoutStatus}
          className="mt-5 inline-block font-eyebrow text-[var(--brand-olive)]"
        >
          Payment received · slàinte mhath
        </span>
        <h1 className="mt-2 font-display text-4xl lg:text-5xl text-[var(--brand-forest)]">
          Thank you, your order is in.
        </h1>
        {data && (
          <p className="mt-6 text-base text-[var(--brand-ink)]">
            <span
              data-testid={HOME.checkoutProductName}
              className="font-semibold"
            >
              {data.product_name}
            </span>{' '}
            ·{' '}
            <span
              data-testid={HOME.checkoutAmount}
              className="font-semibold text-[var(--brand-forest)]"
            >
              {formatGBP(data.amount)}
            </span>
          </p>
        )}
        <p className="mt-4 text-sm text-[var(--brand-muted)] max-w-md mx-auto leading-relaxed">
          A confirmation email will follow shortly.{' '}
          <strong className="text-[var(--brand-forest)]">Free UK delivery on all orders.</strong>{' '}
          Drop-shipped direct from our manufacturing partner &mdash; UK stock
          arrives in <strong>2&ndash;4 working days</strong>, international
          stock in <strong>2&ndash;4 weeks</strong>. We&rsquo;ll keep you
          updated at every step.
        </p>
        <button
          data-testid={HOME.checkoutContinueShopping}
          onClick={onBack}
          className="mt-8 btn-gold px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to the shop
        </button>
      </div>
    );
  }

  // Address-capture form, shown immediately after Stripe confirms payment
  return (
    <div
      data-testid="checkout-address-form"
      className="bg-white border border-[var(--brand-stone)] p-8 lg:p-12"
    >
      <div className="text-center">
        <CheckCircle2
          size={48}
          className="mx-auto text-[var(--brand-olive)]"
          strokeWidth={1.5}
        />
        <span
          data-testid={HOME.checkoutStatus}
          className="mt-4 inline-block font-eyebrow text-[var(--brand-olive)]"
        >
          Payment received
        </span>
        <h1 className="mt-2 font-display text-3xl lg:text-4xl text-[var(--brand-forest)]">
          One last step &mdash; where shall we send it?
        </h1>
        {data && (
          <p className="mt-3 text-sm text-[var(--brand-ink)]">
            <span
              data-testid={HOME.checkoutProductName}
              className="font-semibold"
            >
              {data.product_name}
            </span>{' '}
            &middot;{' '}
            <span
              data-testid={HOME.checkoutAmount}
              className="font-semibold text-[var(--brand-forest)]"
            >
              {formatGBP(data.amount)}
            </span>
          </p>
        )}
        <p className="mt-3 text-xs text-[var(--brand-muted)] max-w-md mx-auto">
          Drop-shipped direct from our manufacturing partner. UK addresses only at
          launch.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4 text-left">
        <Field
          label="Full name"
          required
          testid="checkout-input-name"
          value={form.full_name}
          onChange={update('full_name')}
          autoComplete="name"
        />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field
            label="Email"
            type="email"
            testid="checkout-input-email"
            value={form.email}
            onChange={update('email')}
            autoComplete="email"
            placeholder="For order updates"
          />
          <Field
            label="Phone"
            type="tel"
            testid="checkout-input-phone"
            value={form.phone}
            onChange={update('phone')}
            autoComplete="tel"
            placeholder="For courier"
          />
        </div>
        <Field
          label="Address line 1"
          required
          testid="checkout-input-line1"
          value={form.line1}
          onChange={update('line1')}
          autoComplete="address-line1"
        />
        <Field
          label="Address line 2"
          testid="checkout-input-line2"
          value={form.line2}
          onChange={update('line2')}
          autoComplete="address-line2"
          placeholder="Optional"
        />
        <div className="grid sm:grid-cols-[1fr_140px] gap-4">
          <Field
            label="Town / City"
            required
            testid="checkout-input-city"
            value={form.city}
            onChange={update('city')}
            autoComplete="address-level2"
          />
          <Field
            label="Postcode"
            required
            testid="checkout-input-postcode"
            value={form.postal_code}
            onChange={update('postal_code')}
            autoComplete="postal-code"
            style={{ textTransform: 'uppercase' }}
          />
        </div>

        {formError && (
          <p
            data-testid="checkout-address-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2"
          >
            {formError}
          </p>
        )}

        <button
          type="submit"
          data-testid="checkout-address-submit"
          disabled={submitting}
          className="w-full btn-gold py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Saving&hellip;
            </>
          ) : (
            <>Confirm delivery address</>
          )}
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, testid, ...props }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-olive)] mb-1.5">
        {label} {required && <span className="text-[var(--brand-gold)]">*</span>}
      </span>
      <input
        data-testid={testid}
        className="w-full bg-[var(--brand-parchment)] border border-[var(--brand-stone)] focus:border-[var(--brand-olive)] focus:outline-none px-3 py-2.5 text-sm text-[var(--brand-ink)] placeholder:text-[var(--brand-muted)]/70 transition-colors"
        {...props}
      />
    </label>
  );
}

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  // 'polling' | 'paid' | 'failed' | 'expired' | 'unknown'
  const [state, setState] = useState(sessionId ? 'polling' : 'unknown');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);
  // Track the pending setTimeout so we can cancel on unmount / new session.
  // Prevents a "setState after unmount" warning and stops the poller
  // continuing after the user navigates away from the success page.
  const pollTimeoutRef = useRef(null);
  const cancelledRef = useRef(false);

  const poll = useCallback(async (id) => {
    if (cancelledRef.current) return;
    try {
      const res = await axios.get(`${API}/checkout/status/${id}`);
      if (cancelledRef.current) return;
      setData(res.data);
      const ps = res.data.payment_status;
      if (ps === 'paid') {
        setState('paid');
        return;
      }
      if (res.data.status === 'expired') {
        setState('expired');
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
        setState('unknown');
        return;
      }
      pollTimeoutRef.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err?.response?.data?.detail || 'Could not check payment status');
      setState('failed');
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    if (sessionId) poll(sessionId);
    return () => {
      // Unmount / sessionId change: stop polling immediately.
      cancelledRef.current = true;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [sessionId, poll]);

  return (
    <Shell>
      <div
        data-testid={HOME.checkoutSuccessPage}
        className="bg-white border border-[var(--brand-stone)] p-10 lg:p-14 text-center"
      >
        {state === 'polling' && (
          <>
            <Loader2
              size={56}
              className="mx-auto text-[var(--brand-olive)] animate-spin"
              strokeWidth={1.5}
            />
            <h1 className="mt-6 font-display text-3xl lg:text-4xl text-[var(--brand-forest)]">
              Confirming your payment…
            </h1>
            <p
              data-testid={HOME.checkoutStatus}
              className="mt-3 text-sm text-[var(--brand-muted)]"
            >
              Just a moment while we hear back from Stripe.
            </p>
          </>
        )}

        {state === 'paid' && (
          <PaidView
            data={data}
            sessionId={sessionId}
            onBack={() => navigate('/')}
          />
        )}

        {(state === 'failed' || state === 'expired' || state === 'unknown') && (
          <>
            <XCircle
              size={56}
              className="mx-auto text-red-700"
              strokeWidth={1.5}
            />
            <h1 className="mt-5 font-display text-3xl lg:text-4xl text-[var(--brand-forest)]">
              We couldn&apos;t confirm your payment.
            </h1>
            <p
              data-testid={HOME.checkoutStatus}
              className="mt-3 text-sm text-[var(--brand-muted)]"
            >
              {error || 'No payment was taken. You can safely try again.'}
            </p>
            <button
              data-testid={HOME.checkoutRetry}
              onClick={() => navigate('/#ebikes')}
              className="mt-7 btn-gold px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} /> Back to the shop
            </button>
          </>
        )}
      </div>
    </Shell>
  );
}

export function CheckoutCancel() {
  const navigate = useNavigate();
  return (
    <Shell>
      <div
        data-testid={HOME.checkoutCancelPage}
        className="bg-white border border-[var(--brand-stone)] p-10 lg:p-14 text-center"
      >
        <XCircle
          size={56}
          className="mx-auto text-[var(--brand-muted)]"
          strokeWidth={1.5}
        />
        <h1 className="mt-5 font-display text-3xl lg:text-4xl text-[var(--brand-forest)]">
          Checkout cancelled.
        </h1>
        <p className="mt-3 text-sm text-[var(--brand-muted)] max-w-md mx-auto leading-relaxed">
          No payment was taken. Your basket is still here whenever you&apos;re ready
          to pick up where you left off.
        </p>
        <button
          data-testid={HOME.checkoutRetry}
          onClick={() => navigate('/#ebikes')}
          className="mt-7 btn-gold px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to the shop
        </button>
      </div>
    </Shell>
  );
}
